import React from "react";
import { motion } from "framer-motion";
import { BottomSheet } from "@/components/ui/bottom-sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useShipping } from "@/contexts/ShippingContext";
import { useReturnsExchange } from "@/contexts/ReturnsExchangeContext";
import { usePrivacy } from "@/contexts/PrivacyContext";
import { X } from "lucide-react";

interface TermsSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const termsSections = [
  {
    title: "General Terms",
    items: [
      {
        question: "What are the basic requirements?",
        answer: "By placing an order, you confirm that you are at least 18 years old or have parental/guardian permission. TEL U ASEGH reserves the right to update these terms at any time without prior notice."
      }
    ]
  },
  {
    title: "Product Information & Availability",
    items: [
      {
        question: "What should I know about product details?",
        answer: "We strive to provide accurate product descriptions, but slight variations in color or texture may occur due to screen differences or fabric characteristics. All products are subject to availability. If an item is out of stock after purchase, we will contact you to arrange an alternative or refund."
      }
    ]
  },
  {
    title: "Pricing & Payments",
    items: [
      {
        question: "What are the payment terms?",
        answer: "All prices are listed in EGP and include applicable taxes unless stated otherwise. We accept secure online payments via credit/debit cards, mobile wallets, and cash on delivery (COD) where applicable. TEL U ASEGH reserves the right to change prices without prior notice."
      }
    ]
  },
  {
    title: "Orders & Cancellations",
    items: [
      {
        question: "What is the order modification policy?",
        answer: "Once an order is placed, you can modify or cancel it within one hour of confirmation. After this period, the order will be processed and cannot be changed. For urgent modifications or cancellations, please contact our support team immediately at help@teluasegh.com or 01044916655. TEL U ASEGH reserves the right to refuse or cancel any order due to suspected fraud or unauthorized payment use."
      }
    ]
  },
  {
    title: "Intellectual Property",
    items: [
      {
        question: "What are the copyright terms?",
        answer: "All content on the TEL U ASEGH website, including images, designs, and text, is owned by TEL U ASEGH and protected under copyright law. Unauthorized use, reproduction, or distribution of any content is strictly prohibited."
      }
    ]
  },
  {
    title: "Limitation of Liability",
    items: [
      {
        question: "What are the liability limitations?",
        answer: "TEL U ASEGH is not responsible for any direct or indirect damages arising from the use of our website or products. We are not liable for delays or losses due to factors beyond our control."
      }
    ]
  }
];

export function TermsSheet({ isOpen, onClose }: TermsSheetProps) {
  const { openShipping } = useShipping();
  const { openReturnsExchange } = useReturnsExchange();
  const { openPrivacy } = usePrivacy();

  const handlePolicyClick = (action: () => void) => {
    onClose(); // Close terms sheet
    action(); // Open the respective policy sheet
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="p-6"
      >
        <div className="flex items-center justify-center mb-6">
          <h2 className="text-2xl font-semibold">Terms & Conditions</h2>
        </div>
        <div className="space-y-6">
          <p className="text-muted-foreground">
            Welcome to TEL U ASEGH. By accessing and using our website, you agree to the following terms and conditions. 
            Please read them carefully before making a purchase.
          </p>
          <Accordion type="single" collapsible className="w-full">
            {termsSections.map((section, index) => (
              <div key={section.title} className="mb-6">
                <h3 className="text-lg font-medium mb-4">{section.title}</h3>
                {section.items.map((item, itemIndex) => (
                  <AccordionItem key={itemIndex} value={`${index}-${itemIndex}`}>
                    <AccordionTrigger className="text-left">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent>{item.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </div>
            ))}
          </Accordion>

          <div className="space-y-4 mt-8">
            <h3 className="text-lg font-medium">Related Policies</h3>
            <div className="space-y-2">
              <Button
                variant="link"
                className="text-primary hover:text-primary/80 p-0 h-auto"
                onClick={() => handlePolicyClick(openShipping)}
              >
                Shipping Policy
              </Button>
              <br />
              <Button
                variant="link"
                className="text-primary hover:text-primary/80 p-0 h-auto"
                onClick={() => handlePolicyClick(openReturnsExchange)}
              >
                Returns & Exchange Policy
              </Button>
              <br />
              <Button
                variant="link"
                className="text-primary hover:text-primary/80 p-0 h-auto"
                onClick={() => handlePolicyClick(openPrivacy)}
              >
                Privacy Policy
              </Button>
            </div>
          </div>
          
          <div className="flex justify-center mt-8">
            <Button 
              type="button"
              variant="default" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="px-8"
            >
              Close
            </Button>
          </div>
        </div>
      </motion.div>
    </BottomSheet>
  );
} 